/*
 * Made to Order page: category filter chips.
 * Every piece has data-keys (its category and subsection names). A chip with data-key shows only the pieces
 * with that key ("all" shows everything). The filter lives in the address after a "#", so the furniture page
 * can link straight to one (/collections/made-to-order#sofa) and the back button works.
 * Chips inside a [data-parent] row are the subsections; that row is shown while its category is selected.
 * The dark "ask us" tile widens to fill whatever is left of the last row.
 * The collection comes in pages of up to 48 pieces. Once the page is up, the other pages are fetched in the
 * background and their pieces are added to the grid, so a filter always covers every piece, not just one page.
 * Without JavaScript every piece on the page is visible, the chips are plain links and the page links work.
 */
(function () {
  'use strict';

  function init(root) {
    if (root.getAttribute('data-udm-ready')) return;
    root.setAttribute('data-udm-ready', 'true');

    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-key]'));
    var subRows = Array.prototype.slice.call(root.querySelectorAll('[data-parent]'));
    var grid = root.querySelector('.udm-grid');
    var fill = root.querySelector('.udm-ask');
    var empty = root.querySelector('[data-udm-empty]');
    var countEl = root.querySelector('[data-udm-count]');
    var items = [];
    var itemKeys = [];
    var shown = 0;
    var currentKey = 'all';

    // (Re)reads the pieces in the grid and the number next to each chip.
    function collect() {
      items = Array.prototype.slice.call(grid.querySelectorAll('[data-keys]'));
      itemKeys = items.map(function (item) {
        return (item.getAttribute('data-keys') || '').split(/\s+/).filter(Boolean);
      });
      chips.forEach(function (chip) {
        var key = chip.getAttribute('data-key');
        var total = key === 'all' ? items.length : itemKeys.filter(function (keys) { return keys.indexOf(key) > -1; }).length;
        var label = chip.querySelector('.udm-chip__n');
        if (label) label.textContent = total;
        if (chip.hasAttribute('data-hide-empty')) chip.hidden = total === 0 && key !== currentKey;
      });
    }

    function isKnown(key) {
      return chips.some(function (chip) { return chip.getAttribute('data-key') === key; });
    }

    // The top-level category a key belongs to (a subsection belongs to the row it sits in).
    function categoryOf(key) {
      if (key === 'all') return 'all';
      var chip = chips.filter(function (c) { return c.getAttribute('data-key') === key; })[0];
      var row = chip && chip.closest('[data-parent]');
      return row ? row.getAttribute('data-parent') : key;
    }

    function fitFill() {
      if (!fill || !grid) return;
      if (shown === 0) {
        fill.hidden = true;
        return;
      }
      fill.hidden = false;
      var columns = getComputedStyle(grid).gridTemplateColumns.split(' ').length || 1;
      var left = shown % columns;
      fill.style.gridColumn = 'span ' + (left === 0 ? columns : columns - left);
    }

    function apply(key) {
      if (!isKnown(key)) key = 'all';
      currentKey = key;
      shown = 0;
      items.forEach(function (item, index) {
        var on = key === 'all' || itemKeys[index].indexOf(key) > -1;
        item.hidden = !on;
        if (on) shown++;
      });

      var category = categoryOf(key);
      chips.forEach(function (chip) {
        var inRow = !!chip.closest('[data-parent]');
        var active = inRow ? chip.getAttribute('data-key') === key : chip.getAttribute('data-key') === category;
        chip.classList.toggle('is-active', active);
        if (active) chip.setAttribute('aria-current', 'true');
        else chip.removeAttribute('aria-current');
      });
      subRows.forEach(function (row) {
        row.hidden = row.getAttribute('data-parent') !== category;
      });

      if (empty) empty.hidden = shown !== 0;
      if (countEl) countEl.textContent = shown + (shown === 1 ? ' piece' : ' pieces');
      fitFill();
    }

    // Other "#" links on the page (the "ask us" tile goes to #ask) are not filters, so they leave the filter alone.
    function fromHash() {
      var key = (window.location.hash || '').replace('#', '');
      if (key && !isKnown(key)) return;
      apply(key || 'all');
    }

    function fetchPage(number) {
      var url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('page', number);
      return fetch(url.toString(), { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('page ' + number + ': ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var nodes = Array.prototype.slice.call(doc.querySelectorAll('.udm-grid > [data-keys]'));
          return { number: number, nodes: nodes.map(function (node) { return document.importNode(node, true); }) };
        });
    }

    // Brings in the pieces of the other pages, in page order, in front of the dark tile.
    function loadOtherPages() {
      var pages = parseInt(grid.getAttribute('data-udm-pages'), 10) || 1;
      var page = parseInt(grid.getAttribute('data-udm-page'), 10) || 1;
      if (pages < 2 || !window.fetch || !window.Promise || !window.URL || !window.DOMParser) return;

      root.classList.add('is-loading');
      var jobs = [];
      for (var number = 1; number <= pages; number++) {
        if (number !== page) jobs.push(fetchPage(number));
      }
      Promise.all(jobs)
        .then(function (results) {
          var byPage = {};
          byPage[page] = items.slice();
          results.forEach(function (result) { byPage[result.number] = result.nodes; });
          var anchor = grid.querySelector('.udm-ask, [data-udm-empty]');
          for (var n = 1; n <= pages; n++) {
            (byPage[n] || []).forEach(function (node) { grid.insertBefore(node, anchor); });
          }
          var pager = root.querySelector('[data-udm-pager]');
          if (pager) pager.hidden = true;
          collect();
          apply(currentKey);
        })
        .catch(function () {
          // Keep what is on the page; the page links stay, so the rest is one click away.
        })
        .then(function () {
          root.classList.remove('is-loading');
        });
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function (event) {
        event.preventDefault();
        var key = chip.getAttribute('data-key');
        if (window.history && history.pushState) {
          history.pushState(null, '', key === 'all' ? window.location.pathname + window.location.search : '#' + key);
        }
        apply(key);
      });
    });
    window.addEventListener('hashchange', fromHash);
    window.addEventListener('popstate', fromHash);
    window.addEventListener('resize', fitFill);

    var firstKey = (window.location.hash || '').replace('#', '');
    currentKey = isKnown(firstKey) ? firstKey : 'all';
    collect();
    apply(currentKey);
    loadOtherPages();
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-udm]'), init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
